import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';
import {
  Person,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

export const ProfileSettings: React.FC = () => {
  const brandColors = useBrandColors();
  const { state: authState, updateUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    bio: '',
    avatar: null as string | null,
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const profile = await apiService.getProfile();
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        jobTitle: profile.jobTitle || '',
        department: profile.department || '',
        bio: profile.bio || '',
        avatar: profile.avatar || null,
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data');
      // Fallback to auth state data
      setFormData({
        name: authState.user?.name || '',
        email: authState.user?.email || '',
        phone: '',
        jobTitle: '',
        department: '',
        bio: '',
        avatar: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setError('');
      const updatedProfile = await apiService.updateProfile(formData);
      
      // Update the user in AuthContext so the navigation shows the new name
      if (authState.user) {
        updateUser({
          ...authState.user,
          name: updatedProfile.name,
          email: updatedProfile.email
        });
      }
      
      setSaveMessage('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear the message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to save profile');
    }
  };

  const handleCancel = () => {
    // Reset to original data
    loadProfile();
    setIsEditing(false);
    setError('');
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setAvatarUploading(true);
      setError('');
      const result = await apiService.uploadAvatar(file);
      
      // Update form data with new avatar
      setFormData(prev => ({
        ...prev,
        avatar: result.avatar
      }));
      
      // Update the user in AuthContext so the navigation shows the new avatar
      if (authState.user) {
        updateUser({
          ...authState.user,
          avatar: result.avatar
        });
      }
      
      setSaveMessage('Avatar updated successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Person sx={{ fontSize: 32, color: brandColors.primary, mr: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Profile Settings
        </Typography>
      </Box>

      {saveMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>Loading profile...</Typography>
        </Box>
      ) : (
        <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Profile Picture Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              src={formData.avatar || undefined}
              sx={{
                width: 100,
                height: 100,
                fontSize: 36,
                backgroundColor: brandColors.primary,
                mr: 3,
                cursor: isEditing ? 'pointer' : 'default',
                '&:hover': isEditing ? { opacity: 0.8 } : {},
              }}
              onClick={handleAvatarClick}
              onError={(e) => {
                console.error('Avatar failed to load:', formData.avatar);
                console.error('Error event:', e);
                // Avatar component handles broken images automatically
              }}
            >
              {!formData.avatar && (formData.name.charAt(0).toUpperCase() || 'U')}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Profile Picture
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a new profile picture to personalize your account
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                size="small"
                disabled={!isEditing || avatarUploading}
                onClick={handleAvatarClick}
              >
                {avatarUploading ? 'Uploading...' : 'Change Photo'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Profile Information */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Personal Information
            </Typography>
            {!isEditing ? (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  sx={{ backgroundColor: brandColors.primary, '&:hover': { backgroundColor: brandColors.secondary } }}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Full Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{ flex: 1, minWidth: 250 }}
              />
              <TextField
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                type="email"
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{ flex: 1, minWidth: 250 }}
              />
              <TextField
                label="Job Title"
                value={formData.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
            <TextField
              fullWidth
              label="Department"
              value={formData.department}
              onChange={(e) => handleInputChange('department', e.target.value)}
              disabled={!isEditing}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Bio"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              disabled={!isEditing}
              variant="outlined"
              multiline
              rows={4}
              placeholder="Tell us a bit about yourself..."
            />
          </Box>
        </CardContent>
      </Card>
      )}
    </Box>
  );
};
